import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { PageHeader } from "@/components/common";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/store/auth";

const SECTIONS = [
  { key: "security", label: "Security" },
  { key: "notifications", label: "Notifications" },
];

export default function Settings() {
  const { user } = useAuth();
  const [active, setActive] = useState("security");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings");
      setData(r.data?.data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { section: active, data: data[active] || {} });
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const changePw = async () => {
    if (pwForm.new_password.length < 8) return toast.error("Password too short");
    try {
      await api.post("/auth/change-password", pwForm);
      toast.success("Password changed");
      setPwForm({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin inline"/></div>;

  const setVal = (k, v) => setData(d => ({ ...d, [active]: { ...(d[active] || {}), [k]: v } }));
  const cur = data[active] || {};

  return (
    <div data-testid="settings-page">
      <PageHeader title="System Settings" breadcrumbs={["System", "Settings"]}
        actions={<button onClick={save} disabled={saving} className="erp-btn-primary inline-flex items-center gap-2" data-testid="save-settings-btn">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
        </button>}
      />

      <div className="border-b border-slate-200 mb-4">
        <div className="flex space-x-6 text-sm font-medium">
          {SECTIONS.map(s => (
            <button key={s.key} onClick={() => setActive(s.key)}
              className={`pb-2 ${active === s.key ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`settings-tab-${s.key}`}
            >{s.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-head"><h3><span className="dot" /><span className="capitalize">{active}</span> Configuration</h3></div>
          <div className="card-body space-y-3">
            {Object.entries(cur).map(([k, v]) => (
              <div key={k}>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{k.replace(/_/g, " ")}</label>
                {typeof v === "boolean" ? (
                  <label className="flex items-center gap-2 mt-1 text-sm">
                    <input type="checkbox" checked={v} onChange={e => setVal(k, e.target.checked)}/>
                    <span>{v ? "Enabled" : "Disabled"}</span>
                  </label>
                ) : typeof v === "number" ? (
                  <input type="number" className="erp-input mt-1" value={v} onChange={e => setVal(k, Number(e.target.value))}/>
                ) : (
                  <input className="erp-input mt-1" value={v || ""} onChange={e => setVal(k, e.target.value)}/>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3><span className="dot" />Account</h3></div>
          <div className="card-body">
          <div className="text-sm text-slate-600 mb-4">Signed in as <b>{user?.name}</b> ({user?.email}) · Role: <b>{user?.role_name}</b></div>
          <h4 className="text-sm font-semibold text-slate-800 mt-4 mb-2">Change Password</h4>
          <div className="space-y-3">
            <input type="password" placeholder="Current password" className="erp-input"
              value={pwForm.current_password} onChange={e => setPwForm({...pwForm, current_password: e.target.value})}
              data-testid="cur-pw-input"
            />
            <input type="password" placeholder="New password (min 8)" className="erp-input"
              value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password: e.target.value})}
              data-testid="new-pw-input"
            />
            <button onClick={changePw} className="erp-btn-primary w-full" data-testid="change-pw-btn">Change Password</button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
